import { useEffect, useState } from 'react'
import type { MouseEvent, PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { FAVORITES_MAX } from '@dorixona/api/favorites'
import { Press, cx } from '@dorixona/ui'
import { Icon } from '../../../ui/Icon'
import { useFavorites, useToggleFavorite } from '../api'
import { hasFavorite, toggleFailure } from '../model'

export interface FavoriteButtonProps {
  productId: number
  /**
   * Где стоит «сердце»: на плитке товара — круглое поверх картинки, в шапке
   * карточки товара — рядом с «Поделиться».
   */
  place?: 'tile' | 'head'
}

/** Сколько висит подпись об отказе: прочитать успевают, экран не засоряется. */
const NOTE_MS = 2500

/**
 * «Сердце» избранного. Это кнопка-переключатель, а не тумблер: вне списка
 * HIG советует именно так (toggles.md › Outside of a list, use a button that
 * behaves like a toggle). Состояние видно заливкой, а не только цветом, и
 * подписью для читалки экрана: «Добавить» / «Убрать из избранного».
 *
 * Нажатие меняет список сразу; отказ откатывает его и коротко говорит, что
 * не вышло. Отдача — `select`: палец узнаёт о переключении раньше глаза.
 * Внутри плитки нажатие не открывает товар — касание до плитки не доходит.
 */
export function FavoriteButton({ productId, place = 'tile' }: FavoriteButtonProps) {
  const { t } = useTranslation('catalog')
  const favorites = useFavorites()
  const toggle = useToggleFavorite()
  const [note, setNote] = useState<'full' | 'failed' | null>(null)
  const on = hasFavorite(favorites.data, productId)

  useEffect(() => {
    if (!note) return
    const timer = setTimeout(() => {
      setNote(null)
    }, NOTE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [note])

  // Плитка — сама кнопка «открыть товар»: ни нажатие, ни его отклик до неё не доходят.
  const stop = (event: MouseEvent | PointerEvent) => {
    event.stopPropagation()
  }

  return (
    <span className={cx('fav-wrap', `fav-wrap--${place}`)}>
      {note ? (
        <span className="fav-note" role="status">
          {note === 'full' ? t('favorites.full', { max: FAVORITES_MAX }) : t('favorites.failed')}
        </span>
      ) : null}
      <Press
        squish="icon"
        haptic="select"
        className={cx('fav', on && 'on')}
        aria-label={t(on ? 'favorites.remove' : 'favorites.add')}
        onClick={stop}
        onPointerDown={stop}
        onPress={() => {
          setNote(null)
          toggle.mutate(
            { productId, on: !on },
            {
              onError: (error) => {
                setNote(toggleFailure(error))
              },
            },
          )
        }}
      >
        <Icon name="i-heart" size={20} />
      </Press>
    </span>
  )
}
